import { C } from "../../../lib/colors.js";

const TRACK_BOTTOM = 26;

const TRACK_LABEL = {
  checking_now: "Checking now",
  not_checked: "Waiting for the first check",
  not_found: "Looking for the answer",
  no_answer: "Stopped looking",
  unknown: "Waiting for the answer",
  found: "Answer found",
};

function dayOf(ms) {
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * The search for a closed question's answer, along the bottom of the timeline: a dotted line
 * from the close, a dot where Sayer last looked and a hollow one where it will look next. Once it
 * has stopped looking, the line ends at its last check.
 *
 * @param {object} props
 * @param {object} props.check lib/forecast/answerCheck.js answerCheckState
 * @param {number} props.closeMs the question's close
 * @param {number} props.todayMs the timeline's today
 * @param {(ms: number) => number} props.xAtMs
 * @param {number} props.vw the visible width
 */
export default function AnswerCheckTrack({ check, closeMs, todayMs, xAtMs, vw }) {
  const x0 = xAtMs(closeMs);
  const endMs = check.kind === "no_answer" && check.lastCheckedAt ? check.lastCheckedAt : todayMs;
  const x1 = xAtMs(Math.max(endMs, closeMs));
  if (x1 < -20 || x0 > vw + 20) return null;
  // A check made just before this browser's close (the resolver's first, at the end of the UTC
  // day) sits on the track's start rather than left of it.
  const lastX = check.lastCheckedAt ? Math.max(x0, xAtMs(check.lastCheckedAt)) : null;
  const nextX = check.nextCheckAt ? xAtMs(check.nextCheckAt) : null;
  const dot = (x, hollow, title, key) => (
    <span
      key={key}
      title={title}
      style={{
        position: "absolute",
        left: x - 5,
        bottom: TRACK_BOTTOM - 5,
        width: 10,
        height: 10,
        boxSizing: "border-box",
        borderRadius: "50%",
        border: `1.5px solid ${C.grayL}`,
        background: hollow ? C.bg : C.grayL,
        pointerEvents: "auto",
      }}
    />
  );
  return (
    <div
      data-answer-check-track={check.kind}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 5,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: x0,
          width: Math.max(0, x1 - x0),
          bottom: TRACK_BOTTOM,
          borderTop: `1.5px dotted ${C.gray}`,
        }}
      />
      <span
        style={{
          position: "absolute",
          // Near the right edge the label sits left of the close, so it never runs off screen.
          ...(x0 > vw - 260 ? { right: vw - x0 + 8 } : { left: x0 + 8 }),
          bottom: TRACK_BOTTOM + 7,
          fontSize: 12,
          color: C.gray,
          whiteSpace: "nowrap",
        }}
      >
        {TRACK_LABEL[check.kind]}
      </span>
      {lastX != null &&
        dot(lastX, false, `Checked ${dayOf(check.lastCheckedAt)}: answer not found`, "last")}
      {nextX != null &&
        check.nextCheckAt > todayMs &&
        dot(nextX, true, `Next check ${dayOf(check.nextCheckAt)}`, "next")}
    </div>
  );
}
